import { WindowEventDetailMap, WrapperApi, WrapperSlotApi } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { isNotNull } from '@mbrtargeting/metatag-utils';
import { GLOBAL_TARGETING } from '../../../core/token.js';
import { getIdsForAdSlot } from '../../../core/utils/adslot-data-store.js';
import { inject } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectCommonSettings } from '../../../loader/essentials/config-selector.js';
import { triggerEvent } from '../../../loader/essentials/events.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, METATAG } from '../../../loader/token.js';
import { WeakMap2 } from '../../../utils/weak-map2.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';

const log = logger.logGroup({ prefix: 'PublicWrapperApi' });

export class PublicWrapperApi implements WrapperApi {

    @inject(METATAG) private metaTag!: MetaTag;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;
    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;

    #wrapperSlotFacades = new WeakMap2<Adslot, WrapperSlotApi>;

    constructor() {
        this.metaTag.Wrapper = this;
    }

    public getFilteredKeyValues(): Record<string, string[]> {
        const { kvFilter = [] } = this.configResolver?.get(selectCommonSettings()) || {};
        return this.globalTargeting.filter(([key]) => !kvFilter.includes(key)).toObject();
    };

    public triggerEvent<K extends keyof WindowEventDetailMap>(eventName: K, passedObject: WindowEventDetailMap[K]) {
        triggerEvent(eventName, passedObject);
    }

    public getSlotByName(slotName: string): WrapperSlotApi | null {
        const slot = this.adslotRegistry.getAdslot(slotName) as Adslot;
        if (isNotNull(slot)) {
            return this.#wrapperSlotFacades.getOrInsertComputed(slot, createWrapperSlotFacade) ?? null;
        }
        return null;
    }
}

const createWrapperSlotFacade = (slot: Adslot): WrapperSlotApi => ({
    get slotName() { return slot.slotName },
    get containerNode() { return slot.containerNode },
    get localTargeting() { return slot.localTargeting },
    setTargeting: (keyValues: Record<string, string | string[]>) => void (slot.setTargeting(keyValues)),
    removeTargeting: (key: string, value?: string) => void (slot.removeTargeting(key, value)),
    getPageType: () => slot.getPageType(),
    getZone: () => slot.getZone(),
    getAdUnits: () => slot.getAdUnits(),
    getAdUnitPath: () => slot.getAlias(),
    renderAd: (callback: (targetDiv: HTMLDivElement) => void) => slot.renderAd(callback),
    getFilteredKeyValues: () => slot.getFilteredKeyValues(),
    getIdsForAdSlot: () => getIdsForAdSlot(slot.slotName),
    reportAdDelivery: (width: number | string, height: number | string, priorityRequest: boolean = false) => void (slot.reportAdDelivery(width, height, priorityRequest)),
    getAdServerNodeId: () => slot.getAdServerNodeId(),
    getAdServerNode: () => slot.getAdServerNode()
});
